// "Bring my data from Portfolio Ledger": reads a file from the old app, merges it into what's already
// here without duplicates, and writes the old formats back out. Pure functions, tested on their own
// and against the real database.
//
// Files it understands:
//   - the Drive data file  portfolio-ledger-data.json   { rev, doc, history, intraday, savedAt }  (complete)
//   - a backup             portfolio-backup-….json      the old app's in-memory ledger (no hourly points)
//   - a dividend update    pf-dividends-update-….json   { kind: "pf-dividends", events: [...] }

import {
  BACKUP_KNOWN, RECORD_LISTS, blankLedger, docJson, evId, sanitizeDoc, validDay, validHour,
  type DayPoint, type Doc, type Fx, type HourPoint, type Ledger, type Rec, type Upcoming,
} from './ledger';

export type FileKind = 'datafile' | 'backup' | 'dividends';
export const DATAFILE_KNOWN = ['rev', 'doc', 'history', 'intraday', 'savedAt'];

export interface Parsed {
  kind: FileKind;
  ledger: Ledger;                 // for 'dividends', an empty ledger
  dividendEvents: Record<string, unknown>[];
  rejected: { history: number; hourly: number };
  fixedIds: number;               // records given an id because theirs was missing or repeated
}

export class ImportError extends Error {}

const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);

/** Reads a file's text. Throws ImportError with the old app's wording when it isn't one of ours. */
export function parseImport(text: string): Parsed {
  let j: unknown;
  try { j = JSON.parse(text); } catch { throw new ImportError('That file isn’t a valid backup.'); }
  if (!isObj(j)) throw new ImportError('That file isn’t a Portfolio Ledger backup.');
  const out: Parsed = { kind: 'backup', ledger: blankLedger(), dividendEvents: [], rejected: { history: 0, hourly: 0 }, fixedIds: 0 };

  if (j.kind === 'pf-dividends' && Array.isArray(j.events)) {
    out.kind = 'dividends';
    out.dividendEvents = j.events.filter(isObj);
    return out;
  }
  let hist: unknown, hourly: unknown = [];
  if ('doc' in j && ('rev' in j || 'history' in j)) {
    out.kind = 'datafile';
    out.ledger.doc = sanitizeDoc(j.doc ?? {});
    hist = j.history;
    hourly = j.intraday;
    for (const k of Object.keys(j)) if (!DATAFILE_KNOWN.includes(k)) out.ledger.fileExtra[k] = j[k];
  } else if (Array.isArray(j.txns) && Array.isArray(j.income)) {
    out.kind = 'backup';
    out.ledger.doc = sanitizeDoc(j, BACKUP_KNOWN);
    hist = j.history;
    // Typed prices and a typed rate come along (Q12). Live prices don't: the price service has fresher ones.
    if (isObj(j.prices)) for (const [k, v] of Object.entries(j.prices)) if (isObj(v) && v.src === 'manual' && v.price != null && v.price !== '') out.ledger.manualPrices[k] = v;
    if (isObj(j.fx) && j.fx.src === 'manual' && +(j.fx.rate as number) > 0) out.ledger.fxManual = j.fx as unknown as Fx;
  } else {
    throw new ImportError('That file isn’t a Portfolio Ledger backup.');
  }
  const hs = Array.isArray(hist) ? hist : [];
  out.ledger.history = sortDays(hs.filter(validDay));
  out.rejected.history = hs.length - out.ledger.history.length;
  const hr = Array.isArray(hourly) ? hourly : [];
  out.ledger.intraday = hr.filter(validHour).sort((a, b) => a.t - b.t);
  out.rejected.hourly = hr.length - out.ledger.intraday.length;
  out.fixedIds = fixIds(out.ledger.doc);
  return out;
}

const sortDays = (h: DayPoint[]) => {
  const m = new Map<string, DayPoint>();
  for (const p of h) m.set(p.d, p);            // a repeated day: the later entry wins, as the old server did
  return [...m.values()].sort((a, b) => (a.d < b.d ? -1 : a.d > b.d ? 1 : 0));
};

/** Every record needs an id that's unique in its list (the database requires it). The old app always
 *  gave one; if a file somehow lacks one, a stable id is made from the record's contents, and a repeated
 *  id gets a suffix, so nothing is dropped and re-importing gives the same ids. */
function fixIds(doc: Doc): number {
  let n = 0;
  for (const list of RECORD_LISTS) {
    const seen = new Set<string>();
    doc[list] = doc[list].map((r) => {
      let id = typeof r.id === 'string' && r.id ? r.id : typeof r.id === 'number' ? String(r.id) : '';
      if (!id) id = 'imp-' + hash(JSON.stringify(r));
      let k = 2;
      const base = id;
      while (seen.has(id)) id = `${base}~${k++}`;
      seen.add(id);
      if (id !== r.id) { n++; return { ...r, id } as Rec; }
      return r;
    });
  }
  return n;
}
function hash(s: string): string {
  let h1 = 0x811c9dc5, h2 = 0x01000193;
  for (let i = 0; i < s.length; i++) { const c = s.charCodeAt(i); h1 = Math.imul(h1 ^ c, 16777619); h2 = Math.imul(h2 + c, 2246822519); }
  return ((h1 >>> 0).toString(36) + (h2 >>> 0).toString(36)).slice(0, 12);
}

// ---------- merging ----------

export interface Count { added: number; updated: number; unchanged: number }
export interface MergeSummary {
  txns: Count; income: Count; stakes: Count; cash: Count; upcoming: Count; assets: Count;
  dismissed: number; history: Count; hourly: Count; manualPrices: number;
}
const count = (): Count => ({ added: 0, updated: 0, unchanged: 0 });

/** Deep equality for plain JSON values (key order doesn't matter). */
export function same(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b || a === null || b === null || typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a)) return a.length === (b as unknown[]).length && a.every((x, i) => same(x, (b as unknown[])[i]));
  const ka = Object.keys(a as object).filter((k) => (a as Record<string, unknown>)[k] !== undefined);
  const kb = Object.keys(b as object).filter((k) => (b as Record<string, unknown>)[k] !== undefined);
  return ka.length === kb.length && ka.every((k) => same((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/** Merges by key: a record already here is replaced by the file's version when they differ (the file
 *  is assumed newer), new ones are added at the end, and nothing is ever removed. */
function mergeBy<T>(cur: T[], inc: T[], key: (x: T) => string, c: Count): T[] {
  const out = cur.slice(), at = new Map(out.map((x, i) => [key(x), i]));
  for (const x of inc) {
    const k = key(x), i = at.get(k);
    if (i == null) { at.set(k, out.length); out.push(x); c.added++; }
    else if (same(out[i], x)) c.unchanged++;
    else { out[i] = x; c.updated++; }
  }
  return out;
}

export function mergeLedger(current: Ledger, incoming: Ledger): { ledger: Ledger; summary: MergeSummary } {
  const s: MergeSummary = {
    txns: count(), income: count(), stakes: count(), cash: count(), upcoming: count(), assets: count(),
    dismissed: 0, history: count(), hourly: count(), manualPrices: 0,
  };
  const a = current.doc, b = incoming.doc;
  const doc: Doc = {
    display: b.display,
    txns: mergeBy(a.txns, b.txns, (r) => r.id, s.txns),
    income: mergeBy(a.income, b.income, (r) => r.id, s.income),
    stakes: mergeBy(a.stakes, b.stakes, (r) => r.id, s.stakes),
    cash: mergeBy(a.cash, b.cash, (r) => r.id, s.cash),
    upcoming: mergeBy(a.upcoming, b.upcoming, (e: Upcoming) => evId(e), s.upcoming),
    dismissed: a.dismissed.slice(),
    assets: { ...a.assets },
    inboxDone: a.inboxDone.slice(),
    inboxLast: newer(a.inboxLast, b.inboxLast),
    extra: { ...a.extra, ...b.extra },
  };
  for (const id of b.dismissed) if (!doc.dismissed.includes(id)) { doc.dismissed.push(id); s.dismissed++; }
  for (const id of b.inboxDone) if (!doc.inboxDone.includes(id)) doc.inboxDone.push(id);
  for (const [k, v] of Object.entries(b.assets)) {
    if (!(k in doc.assets)) s.assets.added++; else if (same(doc.assets[k], v)) s.assets.unchanged++; else s.assets.updated++;
    doc.assets[k] = v;
  }
  const history = sortDays(mergeBy(current.history, incoming.history, (p) => p.d, s.history));
  const intraday = mergeBy(current.intraday, incoming.intraday, (p: HourPoint) => String(p.t), s.hourly).sort((x, y) => x.t - y.t);
  const manualPrices = { ...current.manualPrices };
  for (const [k, v] of Object.entries(incoming.manualPrices)) { if (!same(manualPrices[k], v)) s.manualPrices++; manualPrices[k] = v; }
  return {
    ledger: {
      doc, history, intraday, manualPrices,
      fxManual: incoming.fxManual ?? current.fxManual,
      fileExtra: { ...current.fileExtra, ...incoming.fileExtra },
    },
    summary: s,
  };
}
function newer(x: Record<string, unknown> | null, y: Record<string, unknown> | null) {
  if (!x) return y; if (!y) return x;
  return (+(y.at as number) || 0) >= (+(x.at as number) || 0) ? y : x;
}

/** Plain-English lines for the import confirmation. */
export function describeSummary(s: MergeSummary): string[] {
  const line = (label: string, c: Count) => {
    const bits = [c.added && `${c.added} new`, c.updated && `${c.updated} updated`, c.unchanged && `${c.unchanged} already here`].filter(Boolean);
    return bits.length ? `${label}: ${bits.join(', ')}` : '';
  };
  return [
    line('Trades', s.txns), line('Income payments', s.income), line('Staking positions', s.stakes), line('Cash balances', s.cash),
    line('Upcoming dividends', s.upcoming), line('Asset details', s.assets), line('Daily values', s.history), line('Hourly values', s.hourly),
    s.dismissed ? `Dismissed dividends: ${s.dismissed} new` : '',
    s.manualPrices ? `Typed prices: ${s.manualPrices}` : '',
  ].filter(Boolean);
}

// ---------- exporting to the old formats ----------

/** The Drive data file format, as the old app wrote it. */
export function toDataFile(l: Ledger, rev: number, savedAt: string | null): Record<string, unknown> {
  return { rev, doc: docJson(l.doc), history: l.history, intraday: l.intraday, savedAt, ...l.fileExtra };
}

/** The old app's backup format (Export backup). `livePrices`/`liveFx` are the device's current cache. */
export function toBackup(l: Ledger, livePrices: Record<string, unknown> = {}, liveFx: Fx | null = null): Record<string, unknown> {
  const d = l.doc;
  return {
    v: 2, display: d.display, fx: l.fxManual ?? liveFx,
    txns: d.txns, income: d.income, stakes: d.stakes, cash: d.cash, upcoming: d.upcoming, history: l.history,
    dismissed: d.dismissed, inboxDone: d.inboxDone, inboxLast: d.inboxLast,
    prices: { ...livePrices, ...l.manualPrices }, assets: d.assets, ...d.extra,
  };
}

/** Builds a Ledger from what load_ledger() returns. */
export function fromServer(r: {
  doc: Record<string, unknown> | null; history?: unknown[]; intraday?: unknown[]; manualPrices?: Record<string, unknown>;
  fxManual?: unknown; fileExtra?: Record<string, unknown>;
}): Ledger {
  const l = blankLedger();
  if (r.doc) l.doc = sanitizeDoc(r.doc);
  l.history = (r.history ?? []).filter(validDay);
  l.intraday = (r.intraday ?? []).filter(validHour);
  l.manualPrices = (r.manualPrices ?? {}) as Ledger['manualPrices'];
  l.fxManual = isObj(r.fxManual) ? (r.fxManual as unknown as Fx) : null;
  l.fileExtra = r.fileExtra ?? {};
  return l;
}

